import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { isNil } from 'ramda'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Form } from 'formik'
import { useDispatch } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import { useMutation } from '@tanstack/react-query'
import themesConfig from 'app/fuse-configs/themesConfig'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import {
  Dialog,
  IconButton,
  InputAdornment,
  makeStyles,
  Typography,
} from '@material-ui/core'

import { toSlug } from 'commons/slug'
import effects from 'app/effects/effects'
import { CopyIcon } from 'app/components/icons'
import { copyToClipboard } from 'libs/clipboard'
import { messageAlertError } from 'commons/error'
import MessageAlert from 'app/components/MessageAlert'
import { CLOSE, SECONDARY } from 'app/constants/button'
import { mapBoolToNum, mapNumToBool } from 'commons/boolean'

import Switchs from 'app/componentV4/Switchs'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'

const useStyles = makeStyles({
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
    '& .MuiOutlinedInput-inputAdornedStart': {
      padding: '6px 0',
    },
    '& .MuiInputAdornment-positionStart': {
      marginRight: 'unset',
    },
  },
})
const LandingPage = ({ placeName }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const endpoint = process.env.REACT_APP_PLACE_URL

  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isCheck, setIsCheck] = useState(false)
  const [arriving, setArriving] = useState(false)
  const [inital, setInital] = useState({
    uniqueName: null,
    isLandingShow: null,
    isLandingCreated: null,
  })

  const fetchLandingPage = useMutation({
    mutationFn: async () => {
      const response = await effects.apis.profileInfo.getLandingPage()
      return response.data
    },
    onSuccess: async ({ isLandingShow, uniqueName, isLandingCreated }) => {
      const isShow = mapNumToBool(isLandingShow)
      setArriving(isShow && !isNil(uniqueName))
      setIsCheck(isShow)
      setInital({
        uniqueName: uniqueName,
        isLandingShow: mapNumToBool(isLandingShow),
        isLandingCreated: mapNumToBool(isLandingCreated),
      })
    },
    onError: (error) => {
      dispatch(
        MessageAlert.showError({
          message: messageAlertError(error, formatMessage),
        }),
      )
    },
  })

  const updateLandingPage = useMutation({
    mutationFn: async ({ uniqueName, isLandingShow }) => {
      const response = await effects.apis.profileInfo.updateLandingPage({
        uniqueName: uniqueName,
        isLandingShow: mapBoolToNum(isLandingShow),
      })
      return response.data
    },
    onSuccess: () => {
      dispatch(
        MessageAlert.showSuccess({
          message: formatMessage({ id: 'text.change.success' }),
        }),
      )
      setOpen(false)
      setLoading(false)
      fetchLandingPage.mutate()
    },
    onError: (error, { isLandingShow }) => {
      dispatch(
        MessageAlert.showError({
          message: messageAlertError(error, formatMessage),
        }),
      )
      setIsCheck(!isLandingShow)
      setLoading(false)
    },
  })

  const handleSwitch = (bool) => {
    setIsCheck(bool)
    setLoading(true)

    if (isNil(inital.uniqueName)) setOpen(true)
    else updateLandingPage.mutate({ isLandingShow: bool })
  }

  const handleCloseDialog = () => {
    setOpen(false)
    setIsCheck(mapNumToBool(fetchLandingPage?.data?.isLandingCreated))
    setLoading(false)
  }

  useEffect(() => {
    fetchLandingPage.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {/* Card */}
      <div className="bg-white p-10 border-1 border-primary-350 rounded-5 rounded-tr-5 overflow-x-auto">
        <div className="flex justify-between items-center">
          <Typography className="text-14 font-500 text-primary items-center flex">
            {formatMessage({
              id: 'profile.info.landing',
            })}
          </Typography>
          {arriving ? (
            <div>
              <IconButton onClick={() => setOpen(true)}>
                <EditOutlinedIcon
                  fontSize="small"
                  style={{ color: themesConfig.default.colors['primary'] }}
                />
              </IconButton>
              <IconButton
                onClick={() =>
                  copyToClipboard(`${endpoint}/${inital.uniqueName}`)
                    .then(() => {
                      dispatch(
                        MessageAlert.showSuccess({
                          message: formatMessage({
                            id: 'notificationTimeline.card.publicLink.shareAlert.copySucceed',
                          }),
                        }),
                      )
                    })
                    .catch(() => {
                      dispatch(
                        MessageAlert.showError({
                          message: formatMessage({
                            id: 'notificationTimeline.card.publicLink.shareAlert.copyFailure',
                          }),
                        }),
                      )
                    })
                }
              >
                <CopyIcon fill="#183560" />
              </IconButton>
            </div>
          ) : null}
        </div>
        <div className="mt-4 space-y-2">
          <Typography className="text-12 text-primary-300">
            <FormattedMessage id="profile.landing.page.title" />
          </Typography>
          {arriving ? (
            <div className="bg-neutral-50 p-3 break-all rounded-6">
              <Typography className="text-14 text-primary-300 mx-3">
                {`${endpoint}/${inital.uniqueName}`}
              </Typography>
            </div>
          ) : null}

          <div className="flex py-5">
            <Switchs
              id="switch-place-public"
              disabled={loading}
              checked={isCheck}
              onChange={(e) => handleSwitch(e.target.checked)}
            />
            <Typography className="text-14 text-primary font-400 mx-3">
              {formatMessage({
                id: 'profile.info.show.landing',
              })}
            </Typography>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={open}
        onClose={() => handleCloseDialog()}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: { borderRadius: 16, padding: '3rem' },
        }}
      >
        <div className="flex justify-between">
          <Typography className="text-16 font-500 text-primary items-center flex">
            <FormattedMessage id="profile.landing.edit.title" />
          </Typography>
          <IconButton
            aria-label="close-opening-hours-dialog"
            onClick={() => handleCloseDialog()}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <Formik
          initialValues={{ uniqueName: inital.uniqueName ?? toSlug(placeName) }}
          validationSchema={Yup.object({
            uniqueName: Yup.string()
              .matches(
                /^[a-zA-Z0-9-_]*$/,
                formatMessage({
                  id: 'non.supported.symbols.text',
                }),
              )
              .required(
                formatMessage({
                  id: 'composeDialog.field.required',
                }),
              ),
          })}
          onSubmit={(values) => {
            updateLandingPage.mutate(
              inital.isLandingCreated
                ? { uniqueName: values.uniqueName }
                : { uniqueName: values.uniqueName, isLandingShow: isCheck },
            )
          }}
        >
          {({ dirty, values, handleChange, handleBlur, touched, errors }) => (
            <Form>
              <div className="my-10">
                <LabelInput
                  label={formatMessage({
                    id: 'profile.landing.url.title',
                  })}
                >
                  <SimpleInput
                    id="landing-page-uniqueName-input"
                    className={classes.input}
                    name="uniqueName"
                    value={values.uniqueName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    startAdornment={
                      <InputAdornment
                        position="start"
                        style={{ userSelect: 'all' }}
                      >
                        {`${endpoint}/`}
                      </InputAdornment>
                    }
                    maxLength={150}
                    inputProps={{
                      maxLength: 150,
                    }}
                    error={touched.uniqueName && Boolean(errors.uniqueName)}
                    helperText={touched.uniqueName && errors.uniqueName}
                  />
                </LabelInput>
              </div>
              <div className="flex justify-center space-x-5">
                <OutlinedButtons
                  color={CLOSE}
                  onClick={() => handleCloseDialog()}
                >
                  <FormattedMessage id="cancelBtn" />
                </OutlinedButtons>
                <ContainedButton
                  type="submit"
                  color={SECONDARY}
                  disabled={
                    (!dirty && !isNil(inital.uniqueName)) ||
                    updateLandingPage.isPending
                  }
                  isLoading={updateLandingPage.isPending}
                >
                  <FormattedMessage id="subscriberCrm.editGroup.save.label" />
                </ContainedButton>
              </div>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}

export default LandingPage

LandingPage.propTypes = {
  placeName: PropTypes.string,
}
